import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { createProcurementPurchaseOrder } from 'api/procurement.api'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { Button, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { ItemRow } from './AddOfferModal'
import { ProcurementPurchaseRequestListResponse } from 'types/procurement'
import { ProcurementPurchaseOrderResponse } from 'types/procurement/purchaseOrder.model'

export const CreatePOButton = ({
  request,
}: {
  request: ProcurementPurchaseRequestListResponse
}) => {
  const [open, setOpen] = useState(false)
  const mutation = useMutation({
    mutationFn: (items: { procurementPurchaseRequestItemId: number; price: number }[]) => {
      if (!items.length) {
        throw new Error('Items are required')
      }
      return createProcurementPurchaseOrder({
        procurementPurchaseRequestId: request.id,
        items,
      })
    },
    onSuccess: () => {
      toast.success('PO created successfully')
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })
  return (
    <>
      <Button
        orange
        width='220'
        onClick={() => {
          if (request.selectedQuote?.items.length < 1) {
            setOpen(true)
          } else {
            mutation.mutate(
              request.selectedQuote?.items.map((item) => ({
                procurementPurchaseRequestItemId: item.procurementPurchaseRequestItem.id,
                price: item.price,
              })),
            )
          }
        }}
      >
        Create PO
      </Button>
      {open && (
        <AddItemPricesModal request={request} onClose={() => setOpen(false)} mutation={mutation} />
      )}
    </>
  )
}

const AddItemPricesModal = ({
  request,
  onClose,
  mutation,
}: {
  request: ProcurementPurchaseRequestListResponse
  onClose: () => void
  mutation: UseMutationResult<
    ProcurementPurchaseOrderResponse,
    Error,
    { procurementPurchaseRequestItemId: number; price: number }[]
  >
}) => {
  const [itemsWithPrices, setItemsWithPrices] = useState<{ id: number; price: number }[]>(
    request?.items.map((item) => ({ id: item.id, price: 0 })) || [],
  )
  return (
    <Modal onClose={onClose}>
      <ModalContent>
        <ModalHeader>
          <TitleText>Add Item Prices</TitleText>
        </ModalHeader>
        <ModalBody>
          <Flex column gap='1rem'>
            {request?.items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                price={itemsWithPrices.find((i) => i.id === item.id)?.price || null}
                setPrice={(price) =>
                  setItemsWithPrices(
                    itemsWithPrices.map((i) => (i.id === item.id ? { ...i, price } : i)),
                  )
                }
              />
            ))}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            orange
            onClick={() =>
              mutation.mutate(
                itemsWithPrices.map((item) => ({
                  procurementPurchaseRequestItemId: item.id,
                  price: item.price,
                })),
              )
            }
          >
            Create PO
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
